import {Stack, Text, TextInput, createStyles} from '@mantine/core';
import assert from 'assert';
import React from 'react';

import LeafTypeSelector from 'data-frames/header-menu/LeafTypeSelector';
import PrimitiveValueEditor, {SupportedLeafType} from 'data-frames/PrimitiveValueEditor';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {MAX_METADATA_LAYER_NAME_LENGTH} from 'samples/plate-maps/PlateMapConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {MetadataLayerData} from '../metadata-layers/machine/MetadataLayerMachine';

// DATETIME is omitted for now (the editor's format doesn't yet match backend datetime validation).
// Keep aligned with the read-back allow-list in ScratchPlateDesignSummaryUtil so layer types round-trip.
const METADATA_ALLOWED_LEAF_TYPES: Array<LeafType> = [
  LeafType.TEXT,
  LeafType.INTEGER,
  LeafType.DECIMAL,
  LeafType.BOOLEAN,
  LeafType.DATE,
];

export interface MetadataLayerSettingsProps {
  activeLayer: MetadataLayerData;
  existingLayerNames: Set<string>;
  pendingValue: string | number | boolean | null;
  hasSelectedWells: boolean;
  onUpdateLayerName: (layerId: string, name: string) => void;
  onUpdateLayerType: (layerId: string, layerType: LeafType) => void;
  onUpdatePendingValue: (value: string | number | boolean | null) => void;
}

const useStyles = createStyles((theme) => ({
  instructionText: {
    color: theme.colors.gray[7],
  },
}));

/** Settings panel for a metadata layer: rename, change its primitive type, and type a value applied to selected wells. */
export default function MetadataLayerSettings({
  activeLayer,
  existingLayerNames,
  pendingValue,
  hasSelectedWells,
  onUpdateLayerName,
  onUpdateLayerType,
  onUpdatePendingValue,
}: MetadataLayerSettingsProps): JSX.Element {
  const {classes} = useStyles();
  assert(activeLayer != null, 'Expected active layer to be defined.');
  const normalizedLayerName = activeLayer.name.trim();
  const isNameUnique = !existingLayerNames.has(normalizedLayerName);
  const isNameEmpty = normalizedLayerName === '';
  const isNameTooLong = normalizedLayerName.length > MAX_METADATA_LAYER_NAME_LENGTH;

  return (
    <Stack spacing="sm" {...getTestProps({component: 'MetadataLayerSettings', element: 'root'})}>
      <Text size="md" className={classes.instructionText}>
        Select wells in the plate to assign a value for this metadata layer.
      </Text>
      <TextInput
        size="md"
        required={true}
        label="Name"
        labelProps={{fw: 'bold'}}
        autoFocus={true}
        onChange={(e) => {
          onUpdateLayerName(activeLayer.layerId, e.target.value);
        }}
        placeholder="Name"
        value={activeLayer.name}
        error={
          isNameEmpty
            ? 'Metadata layer name must not be empty'
            : isNameTooLong
              ? `Metadata layer name must be ${MAX_METADATA_LAYER_NAME_LENGTH} characters or fewer`
              : !isNameUnique
                ? 'Metadata layer names must be unique'
                : undefined
        }
      />
      <div>
        <FieldLabel label="Type " isRequired={true} />
        <LeafTypeSelector
          selectedType={activeLayer.type}
          allowedTypes={METADATA_ALLOWED_LEAF_TYPES}
          onChange={(type) => {
            onUpdateLayerType(activeLayer.layerId, type);
          }}
        />
      </div>
      <div>
        <FieldLabel label="Value " isRequired={true} />
        <PrimitiveValueEditor
          // Remount on type change so the editor's internal value state can't carry a value of the
          // previous (now type-incompatible) primitive type into the new editor.
          key={activeLayer.type}
          // Safe cast: selectable types are constrained by METADATA_ALLOWED_LEAF_TYPES.
          leafType={activeLayer.type as SupportedLeafType}
          value={pendingValue}
          onChange={onUpdatePendingValue}
          disabled={!hasSelectedWells}
        />
        {!hasSelectedWells && (
          <Text size="xs" c="dimmed" mt="xs">
            Select one or more wells to assign a value.
          </Text>
        )}
      </div>
    </Stack>
  );
}
